import { useEffect, useState } from 'react';
import Swal from 'sweetalert2';
// import userService from '../../services/userService'; // Necesitamos servicio para listar usuarios
import api from '../../services/api';
import { cajaService } from '../../services/cajaService'; // Import cajaService
import { habilitacionService } from '../../services/habilitacionService';
import './ModalEntregarEfectivo.css'; // Asegurar estilos propios o reutilizar

import { useAuth } from '../../hooks/useAuth'; // Import useAuth for fallback

const ModalEntregarEfectivo = ({ show, onClose, sesionActual }) => {
    const { user } = useAuth(); // Get authenticated user for fallback

    const [usuariosConCaja, setUsuariosConCaja] = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(false);

    // Formulario
    const [receptorId, setReceptorId] = useState('');
    const [montoTotal, setMontoTotal] = useState('');
    const [observaciones, setObservaciones] = useState('');

    // Estados avanzados de Origen (Replicando ModalGasto)
    const [tipoOrigen, setTipoOrigen] = useState('SESION'); // 'SESION', 'MIXTO'
    const [origenesList, setOrigenesList] = useState([{ key: '', monto: '' }]);
    const [analisisSesion, setAnalisisSesion] = useState(null);
    const [origenDetalle, setOrigenDetalle] = useState(''); // Key del grupo seleccionado

    useEffect(() => {
        if (show) {
            fetchUsuariosActivos();
            fetchSaldosReales();
        }
    }, [show, sesionActual]);

    const fetchSaldosReales = async () => {
        // Fallback: Si sesionActual no tiene usuario_id, usar user.id
        const currentUserId = sesionActual?.usuario_id || user?.id;

        if (!currentUserId) {
            console.error('Cannot fetch balances: No user ID available');
            return;
        }

        try {
            // 1. Obtener Análisis Base
            const analisis = await cajaService.obtenerAnalisisIngresos(currentUserId);

            // 2. Obtener Reporte Global para totales históricos
            const reporteGlobal = await cajaService.obtenerReporteGlobalConceptos({
                usuario_id: currentUserId,
                fechaDesde: '2025-01-01',
                fechaHasta: new Date().toISOString().split('T')[0]
            });

            // 3. Fusionar datos para obtener "pending" (disponible) real
            const cajaGeneral = reporteGlobal.grupos?.find(c => c.key === 'CAJA_GENERAL');
            if (cajaGeneral?.subconceptos?.length > 0) {
                const gruposCorrectos = cajaGeneral.subconceptos.map(c => {
                    const key = `${c.concepto}_${c.year}`;
                    const grupoOriginal = analisis.grupos?.find(g => g.key === key) || {};

                    const efectivo = c.desglose_ingresos?.EFECTIVO || 0;
                    const egresosEfectivo = c.desglose_egresos?.EFECTIVO || 0;

                    // [FIX] Extraer depósitos y habilitaciones enviadas (enviado) del reporte global
                    // Esto asegura que el "Disponible" sea igual al que sale en el Arqueo
                    const deposited = c.desglose_depositos?.EFECTIVO || 0;
                    const enviado = c.desglose_hab_egreso?.EFECTIVO || 0;

                    return {
                        key,
                        label: c.label,
                        total: efectivo,
                        egresos: egresosEfectivo,
                        deposited: deposited,
                        enviado: enviado,
                        // [FIX] Restar depósitos y envíos para obtener el disponible real
                        pending: Math.max(0, efectivo - egresosEfectivo - deposited - enviado),
                        year: c.year
                    };
                });
                analisis.grupos = gruposCorrectos.sort((a, b) => b.total - a.total);
            }
            setAnalisisSesion(analisis);
        } catch (error) {
            console.error('Error fetching real balances:', error);
        }
    };

    const fetchUsuariosActivos = async () => {
        setLoadingUsers(true);
        try {
            // [MOD] Usamos endpoint específico que retorna solo usuarios con CAJA ABIERTA
            const response = await api.get('/caja/usuarios-activos');

            // Get current user ID consistently
            const currentUserId = sesionActual?.usuario_id || user?.id;

            // El backend retorna un array de objetos con { id, username, displayName, caja_sesion_id }
            // Filtramos para no mostrarnos a nosotros mismos
            const otros = response.data.filter(u => u.id !== currentUserId);
            setUsuariosConCaja(otros);
        } catch (error) {
            console.error('Error users:', error);
            // Fallback silencioso vacío
            setUsuariosConCaja([]);
        } finally {
            setLoadingUsers(false);
        }
    };

    // Helpers para modo Mixto
    const handleAddOrigen = () => setOrigenesList([...origenesList, { key: '', monto: '' }]);
    const handleRemoveOrigen = (idx) => {
        const list = [...origenesList];
        list.splice(idx, 1);
        setOrigenesList(list);
    };
    const handleChangeOrigen = (idx, field, val) => {
        const list = [...origenesList];
        list[idx][field] = val;
        setOrigenesList(list);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!receptorId) return Swal.fire('Error', 'Seleccione un destinatario', 'error');
        if (!montoTotal || parseFloat(montoTotal) <= 0) return Swal.fire('Error', 'Monto inválido', 'error');
        if (!receptorId) return Swal.fire('Error', 'Seleccione un destinatario', 'error');
        if (!montoTotal || parseFloat(montoTotal) <= 0) return Swal.fire('Error', 'Monto inválido', 'error');

        // Validaciones según Tipo de Origen
        if (tipoOrigen === 'SESION' && !origenDetalle) return Swal.fire('Error', 'Seleccione el concepto de origen', 'error');

        // Validar saldo suficiente en modo Simple
        if (tipoOrigen === 'SESION' && origenDetalle && analisisSesion) {
            const grupo = analisisSesion.grupos.find(g => g.key === origenDetalle);
            if (grupo && parseFloat(grupo.pending) < parseFloat(montoTotal)) {
                const confirm = await Swal.fire({
                    title: 'Saldo Insuficiente',
                    text: `El concepto "${grupo.label}" solo tiene S/ ${grupo.pending.toFixed(2)}. ¿Continuar con saldo negativo?`,
                    icon: 'warning',
                    showCancelButton: true
                });
                if (!confirm.isConfirmed) return;
            }
        }

        // Validar suma en modo Mixto
        if (tipoOrigen === 'MIXTO') {
            const sum = origenesList.reduce((acc, curr) => acc + (parseFloat(curr.monto) || 0), 0);
            if (Math.abs(sum - parseFloat(montoTotal)) > 0.5) {
                return Swal.fire('Error', `La suma del desglose (S/ ${sum.toFixed(2)}) no coincide con el total (S/ ${montoTotal})`, 'error');
            }
        }

        try {
            // Construir payload de conceptos
            let conceptosPayload = {};

            if (tipoOrigen === 'SESION') {
                conceptosPayload = { [origenDetalle]: parseFloat(montoTotal) };
            } else {
                origenesList.forEach(item => {
                    if (item.key && item.monto) {
                        // Limpiar prefijo SESSION: si existiera por copy-paste, aunque aquí usaremos keys directas
                        const cleanKey = item.key.replace('SESSION:', '');
                        conceptosPayload[cleanKey] = parseFloat(item.monto);
                    }
                });
            }

            await habilitacionService.solicitarHabilitacion({
                emisor_id: sesionActual?.usuario_id || user?.id,
                receptor_id: receptorId,
                monto: parseFloat(montoTotal),
                conceptos: conceptosPayload,
                observaciones: tipoOrigen === 'MIXTO' ? `[MIXTO] ${observaciones}` : observaciones
            });

            Swal.fire({
                icon: 'success',
                title: 'Solicitud Enviada',
                text: 'El dinero se ha descontado de tu caja. El receptor debe aceptarlo.'
            });
            onClose();
        } catch (error) {
            Swal.fire('Error', error.response?.data?.error || 'No se pudo procesar', 'error');
        }
    };

    if (!show) return null;

    return (
        <div className="modal-overlay">
            <div className="modal-container">
                <div className="modal-header">
                    <h3>💸 Entregar Efectivo a Usuario</h3>
                    <button className="btn-close" onClick={onClose}>×</button>
                </div>
                <form onSubmit={handleSubmit} className="modal-body p-4">

                    <div className="mb-3">
                        <label className="form-label">Destinatario (Se requiere aceptación)</label>
                        <select
                            className="form-select"
                            value={receptorId}
                            onChange={(e) => setReceptorId(e.target.value)}
                            required
                        >
                            <option value="">Seleccione Usuario...</option>
                            {usuariosConCaja.map(u => (
                                <option key={u.id} value={u.id}>
                                    {u.displayName || u.username}
                                </option>
                            ))}
                        </select>
                        <small className="text-muted">Solo se muestran usuarios activos en el sistema.</small>
                    </div>

                    {/* SELECCION DE TIPO DE ORIGEN */}
                    <div className="d-flex gap-3 mb-3">
                        <div
                            className={`p-3 border rounded cursor-pointer w-50 text-center ${tipoOrigen === 'SESION' ? 'bg-light border-primary text-primary fw-bold' : ''}`}
                            onClick={() => setTipoOrigen('SESION')}
                            style={{ transition: 'all 0.2s', borderWidth: tipoOrigen === 'SESION' ? '2px' : '1px' }}
                        >
                            <i className="flaticon-381-box me-2"></i> Un solo origen
                        </div>
                        <div
                            className={`p-3 border rounded cursor-pointer w-50 text-center ${tipoOrigen === 'MIXTO' ? 'bg-light border-primary text-primary fw-bold' : ''}`}
                            onClick={() => setTipoOrigen('MIXTO')}
                            style={{ transition: 'all 0.2s', borderWidth: tipoOrigen === 'MIXTO' ? '2px' : '1px' }}
                        >
                            <i className="flaticon-381-layer-1 me-2"></i> Combinado
                        </div>
                    </div>

                    {/* SELECTOR MODO SIMPLE */}
                    {tipoOrigen === 'SESION' && (
                        <div className="mb-3 animate__animated animate__fadeIn">
                            <label className="form-label">
                                Concepto de Origen
                                {origenDetalle && analisisSesion?.grupos?.find(g => g.key === origenDetalle) && (
                                    <span className="ms-2 badge bg-info">
                                        Disp: S/ {analisisSesion.grupos.find(g => g.key === origenDetalle).pending.toFixed(2)}
                                    </span>
                                )}
                            </label>
                            <select
                                className="form-select"
                                value={origenDetalle}
                                onChange={(e) => setOrigenDetalle(e.target.value)}
                                required={tipoOrigen === 'SESION'}
                            >
                                <option value="">-- Seleccione Origen --</option>
                                <option value="GENERAL">General (Sin Clasificar)</option>
                                {analisisSesion?.grupos?.map(g => (
                                    <option key={g.key} value={g.key}>
                                        {g.label} (Disp: S/ {g.pending.toFixed(2)})
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {/* SELECTOR MODO MIXTO */}
                    {tipoOrigen === 'MIXTO' && (
                        <div className="mb-3 p-3 bg-light rounded animate__animated animate__fadeIn">
                            <label className="form-label fw-bold mb-2">Desglose de Fondos</label>
                            {origenesList.map((item, idx) => (
                                <div key={idx} className="d-flex gap-2 mb-2">
                                    <select
                                        className="form-select"
                                        value={item.key}
                                        onChange={(e) => handleChangeOrigen(idx, 'key', e.target.value)}
                                    >
                                        <option value="">-- Fuente --</option>
                                        {analisisSesion?.grupos?.map(g => {
                                            // Disable if selected in another row
                                            const disabled = origenesList.some((o, i) => i !== idx && o.key === g.key);
                                            return (
                                                <option key={g.key} value={g.key} disabled={disabled}>
                                                    {g.label} (Disp: S/ {g.pending.toFixed(2)}) {disabled ? '(Ya seleccionado)' : ''}
                                                </option>
                                            );
                                        })}
                                    </select>
                                    <input
                                        type="number"
                                        className="form-control"
                                        style={{ width: '100px' }}
                                        placeholder="0.00"
                                        value={item.monto}
                                        onChange={(e) => handleChangeOrigen(idx, 'monto', e.target.value)}
                                    />
                                    {origenesList.length > 1 && (
                                        <button type="button" className="btn btn-danger btn-sm" onClick={() => handleRemoveOrigen(idx)}>×</button>
                                    )}
                                </div>
                            ))}
                            <button type="button" className="btn btn-link btn-sm p-0" onClick={handleAddOrigen}>
                                + Agregar otra fuente
                            </button>

                            {/* Validación visual de suma */}
                            <div className="mt-2 text-end">
                                <small className={
                                    Math.abs(origenesList.reduce((s, i) => s + (parseFloat(i.monto) || 0), 0) - (parseFloat(montoTotal) || 0)) < 0.5
                                        ? 'text-success fw-bold'
                                        : 'text-danger fw-bold'
                                }>
                                    Suma: S/ {origenesList.reduce((s, i) => s + (parseFloat(i.monto) || 0), 0).toFixed(2)}
                                    {' '}(Total: {montoTotal || 0})
                                </small>
                            </div>
                        </div>
                    )}

                    <div className="mb-3">
                        <label className="form-label">Monto a Entregar (S/)</label>
                        <input
                            type="number"
                            step="0.01"
                            className="form-control form-control-lg"
                            value={montoTotal}
                            onChange={(e) => setMontoTotal(e.target.value)}
                            required
                        />
                    </div>

                    <div className="mb-3">
                        <label className="form-label">Observaciones</label>
                        <textarea
                            className="form-control"
                            rows="2"
                            value={observaciones}
                            onChange={(e) => setObservaciones(e.target.value)}
                            placeholder="Ej: Entrega parcial del turno mañana..."
                        ></textarea>
                    </div>

                    <div className="d-flex justify-content-end gap-2 mt-4">
                        <button type="button" className="btn btn-secondary" onClick={onClose}>Cancelar</button>
                        <button type="submit" className="btn btn-primary">
                            <i className="flaticon-381-send me-2"></i> Enviar Habilitación
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ModalEntregarEfectivo;
